// frontend/src/components/cspe/PdlSiretEditorModal.jsx
//
// Modale d'édition du SIRET établissement + code APE/NAF d'un PDL (ou PCE).
// Utilisée depuis AnalyseCspe.jsx et AnalyseTicgn.jsx pour fixer le bug
// multi-établissements signalé sur le CERFA 3310 (cas Vannier 2026-05-13).
//
// Comportement :
//  - Préremplit avec accounts.siret / pappers_data.naf_code (le siège) si le
//    PDL n'a pas encore de valeur propre (pdl.siret_etablissement IS NULL).
//  - Valide que le SIRET fait 14 chiffres et commence par le SIREN du compte.
//  - Sauvegarde directe via supabase.from('pdl').update(...) (RLS appliquée).
//
// Réutilisable pour les PCE (gaz) via la prop `table='pce'`.

import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { FiX, FiSave } from "react-icons/fi";
import { supabase } from "../../lib/supabaseClient";

export default function PdlSiretEditorModal({
  open,
  onClose,
  pdl,           // ligne pdl/pce
  client,        // accounts row enrichi (avec pappers_data joint)
  table = "pdl", // 'pdl' ou 'pce'
  onSaved,
}) {
  // ─── Valeurs de contexte (lecture seule)
  const accountSiren = (client?.siren || "").trim();
  const accountName  = client?.company_name || "—";
  const siegeSiret   = (client?.pappers_data?.siret_siege || client?.siret || "").trim();
  const siegeNaf     = (client?.pappers_data?.naf_code || client?.naf_code || "").trim();

  // ─── État local du formulaire
  const [siret, setSiret] = useState("");
  const [naf, setNaf]     = useState("");
  const [saving, setSaving] = useState(false);

  // Préremplir au open / changement de pdl
  useEffect(() => {
    if (!open) return;
    setSiret((pdl?.siret_etablissement || siegeSiret || "").trim());
    setNaf((pdl?.naf_code || siegeNaf || "").trim());
  }, [open, pdl?.id, siegeSiret, siegeNaf]);

  // ─── Validations
  const siretClean = useMemo(() => (siret || "").replace(/\s/g, ""), [siret]);
  const isSiretValid = /^\d{14}$/.test(siretClean);
  const isSirenMatch = !accountSiren || siretClean.startsWith(accountSiren);
  const isIdentiqueSiege = siretClean && siretClean === siegeSiret;
  const isNafIdentiqueSiege = naf && naf === siegeNaf;
  const canSave = isSiretValid && isSirenMatch && !saving && pdl?.id;

  // ─── Sauvegarde
  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from(table)
        .update({
          siret_etablissement: siretClean,
          naf_code: (naf || "").trim() || null,
        })
        .eq("id", pdl.id);
      if (error) throw error;
      toast.success("Identifiants établissement enregistrés");
      if (onSaved) onSaved({ ...pdl, siret_etablissement: siretClean, naf_code: naf || null });
      onClose();
    } catch (e) {
      console.error("[PdlSiretEditorModal] save error:", e);
      toast.error("Erreur : " + (e.message || "enregistrement impossible"));
    } finally {
      setSaving(false);
    }
  };

  if (!open || !pdl) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-start justify-between px-4 py-3 border-b">
          <div>
            <h2 className="text-xs font-semibold text-gray-800">
              Identifiants de l'établissement
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {accountName}
              {accountSiren && (
                <span className="text-gray-400"> — SIREN {accountSiren}</span>
              )}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 -mt-1"
            aria-label="Fermer"
          >
            <FiX className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-4 py-4 space-y-4">
          {/* PDL info (lecture seule) */}
          <div className="text-xs text-gray-600 bg-slate-50 rounded p-2 space-y-0.5">
            <div>
              <span className="font-medium">{table === "pce" ? "PCE" : "PDL"} :</span>{" "}
              <span className="font-mono">
                {pdl?.pdl_number || pdl?.pce_number || "—"}
              </span>
            </div>
            <div>
              <span className="font-medium">Site :</span> {pdl?.site_name || "—"}
            </div>
          </div>

          {/* SIRET établissement */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              SIRET de l'établissement
              <span className="text-gray-400 font-normal"> (14 chiffres)</span>
            </label>
            <input
              type="text"
              value={siret}
              onChange={(e) => setSiret(e.target.value.replace(/\D/g, "").slice(0, 14))}
              className={`w-full px-2 py-1 border rounded text-xs font-mono ${
                siret && !isSiretValid
                  ? "border-red-300 focus:border-red-500"
                  : "border-gray-300 focus:border-blue-500"
              } focus:outline-none focus:ring-1 focus:ring-blue-500`}
              placeholder={siegeSiret || "14 chiffres"}
              inputMode="numeric"
              maxLength={14}
            />
            <div className="mt-1 min-h-[1rem]">
              {siret && !isSiretValid && (
                <p className="text-xs text-red-600">
                  Le SIRET doit contenir exactement 14 chiffres.
                </p>
              )}
              {isSiretValid && !isSirenMatch && (
                <p className="text-xs text-orange-600">
                  ⚠️ Ce SIRET ne commence pas par le SIREN du compte ({accountSiren}). Vérifie qu'il appartient bien à la même entreprise.
                </p>
              )}
              {isIdentiqueSiege && (
                <p className="text-xs text-gray-500">
                  = SIRET du siège de l'entreprise
                </p>
              )}
            </div>
          </div>

          {/* Code APE / NAF */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Code APE / NAF de l'établissement
            </label>
            <input
              type="text"
              value={naf}
              onChange={(e) => setNaf(e.target.value)}
              className="w-full px-2 py-1 border border-gray-300 rounded text-xs font-mono focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              placeholder={siegeNaf || "ex: 10.71C"}
              maxLength={8}
            />
            {isNafIdentiqueSiege && (
              <p className="text-xs text-gray-500 mt-1">
                = Code APE du siège (Pappers)
              </p>
            )}
          </div>

          {/* Aide contextuelle */}
          <div className="text-[10px] text-gray-500 leading-snug">
            Ces informations apparaissent dans le CERFA 3310-TIC (colonne C
            « SIRET éligible » et D « Code APE ») et sur le contrat fournisseur.
            Par défaut, on utilise le SIRET du siège.
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t bg-slate-50 rounded-b-lg">
          <button
            onClick={onClose}
            className="px-2 py-1 text-xs text-gray-600 hover:text-gray-900"
          >
            Annuler
          </button>
          <button
            onClick={handleSave}
            disabled={!canSave}
            className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded text-white transition-colors ${
              canSave
                ? "bg-blue-600 hover:bg-blue-700"
                : "bg-gray-300 cursor-not-allowed"
            }`}
          >
            <FiSave className="h-4 w-4 mr-1.5" />
            {saving ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
}
